import React, { useEffect, useState } from "react";
import {
  Box,
  PageLayout,
  Text,
  ActionList,
  ActionMenu,
  TabNav,
} from "@primer/react";
import {
  PencilIcon,
  TrashIcon,
  PlusIcon,
  SyncIcon,
  KebabHorizontalIcon,
  DownloadIcon,
} from "@primer/octicons-react";
import { Dialog } from "@primer/react/drafts";
import { useSelector } from "react-redux";
import { useCommonContext } from "../../contexts/common";
import { NotifyHelper } from "../../helpers/toast";
import MyButton from "../../components/ui/button/Button";
import DataTable from "../../components/ui/data-table";
import { monThiApi } from "../../api/monThiApi";
import { monThiThanhPhanApi } from "../../api/monThiThanhPhanApi";
import MyModal from "../../components/ui/modal/MyModal";
import Button from "../../components/ui/button";
import MonThiDetailModal from "./detail_modal";
import MonThiThanhPhanDetailModal from "./detail_thanhphan_modal";
import { useCommonSelectedHook } from "../../hooks/useCommonSelectedHook";

interface MonThi {
  id: number;
  mon_thi: string;
  mon_thi_en: string;
  thang_diem: number;
  nhan_xet_chung: boolean;
}

interface MonThiThanhPhan {
  id: number;
  mon_thi: string;
  diem_thanh_phan: string;
  diem_thanh_phan_en: string;
  thang_diem: number;
  nhan_xet_rieng: boolean;
  trong_so: number;
}

const MonThiComponent: React.FC = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [monThis, setMonThis] = useState<MonThi[]>([]);
  const [selectedMonThi, setSelectedMonThi] = useState<MonThi | null>(null);
  const [monThiThanhPhans, setMonThiThanhPhans] = useState<MonThiThanhPhan[]>(
    []
  );
  const [selectedMonThiThanhPhan, setSelectedMonThiThanhPhan] =
    useState<MonThiThanhPhan | null>(null);
  const [showMonThiModal, setShowMonThiModal] = useState(false);
  const [showMonThiThanhPhanModal, setShowMonThiThanhPhanModal] =
    useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showDeleteThanhPhanDialog, setShowDeleteThanhPhanDialog] =
    useState(false);

  const { translate } = useCommonContext();
  const { nam_hoc, dm_coso_id } = useCommonSelectedHook();

  const loadMonThis = async () => {
    setIsLoading(true);
    try {
      const res = await monThiApi.selectByCoSo(dm_coso_id);
      if (res.is_success) {
        setMonThis(res.data);
      }
    } catch (error) {
      NotifyHelper.Error("Có lỗi");
    }
    setIsLoading(false);
  };

  const loadMonThiThanhPhans = async () => {
    if (!selectedMonThi?.id) return;

    setIsLoading(true);
    try {
      const res = await monThiThanhPhanApi.selectByMonThi(selectedMonThi.id);
      if (res.is_success) {
        setMonThiThanhPhans(res.data);
      }
    } catch (error) {
      NotifyHelper.Error("Có lỗi");
    }
    setIsLoading(false);
  };

  useEffect(() => {
    loadMonThis();
  }, [dm_coso_id]);

  useEffect(() => {
    loadMonThiThanhPhans();
  }, [selectedMonThi]);

  const handleDeleteMonThi = async () => {
    if (!selectedMonThi) return;

    try {
      const res = await monThiApi.delete(selectedMonThi.id);
      if (res.is_success) {
        NotifyHelper.Success("Xóa thành công");
        loadMonThis();
      } else {
        NotifyHelper.Error(res.message || "Có lỗi");
      }
    } catch (error) {
      NotifyHelper.Error("Có lỗi");
    }
    setShowDeleteDialog(false);
  };

  const handleDeleteMonThiThanhPhan = async () => {
    if (!selectedMonThiThanhPhan) return;

    try {
      const res = await monThiThanhPhanApi.delete(selectedMonThiThanhPhan.id);
      if (res.is_success) {
        NotifyHelper.Success("Xóa thành công");
        loadMonThiThanhPhans();
      } else {
        NotifyHelper.Error(res.message || "Có lỗi");
      }
    } catch (error) {
      NotifyHelper.Error("Có lỗi");
    }
    setShowDeleteThanhPhanDialog(false);
  };
  const DeleteConfirmDialog = ({
    isOpen,
    onClose,
    onConfirm,
  }: {
    isOpen: boolean;
    onClose: () => void;
    onConfirm: () => void;
  }) => (
    <MyModal
      isOpen={isOpen}
      onClose={onClose}
      title="Base.Label.Warning"
      width="small"
    >
      <Box p={3}>
        <Text>{translate("Base.Message.DeleteConfirm")}</Text>
      </Box>
      <Box display="flex" justifyContent="flex-end" px={3} pb={3}>
        <Button onClick={onClose}>{translate("Base.Label.Cancel")}</Button>
        <Button variant="danger" onClick={onConfirm}>
          {translate("Base.Label.Delete")}
        </Button>
      </Box>
    </MyModal>
  );

  return (
    <Box p={3}>
      <Box display="flex" sx={{ height: "calc(100vh - 240px)" }}>
        {/* Mon Thi Table */}
        <Box flex="1" minWidth="0" sx={{ mr: 3 }}>
          <DataTable
            height="calc(100vh - 200px)"
            data={monThis}
            isLoading={isLoading}
            title="Danh sách môn thi"
            searchEnable
            paging={{ enable: true }}
            selection={{
              mode: "single",
              selectedRowKeys: selectedMonThi ? [selectedMonThi.id] : [],
              onSelectionChanged: (keys: number[]) => {
                const selected = monThis.find((item) => item.id === keys[0]);
                setSelectedMonThi(selected || null);
              },
            }}
            actionComponent={
              <Box sx={{ display: "flex" }}>
                <MyButton
                  text="Thêm môn thi"
                  leadingVisual={PlusIcon}
                  size="medium"
                  variant="primary"
                  onClick={() => {
                    setSelectedMonThi({ id: 0 } as MonThi);
                    setShowMonThiModal(true);
                  }}
                />
                <MyButton
                  text="Base.Label.Refresh"
                  leadingVisual={SyncIcon}
                  size="medium"
                  sx={{ ml: 2 }}
                  onClick={loadMonThis}
                />
              </Box>
            }
            columns={[
              {
                id: "cmd",
                caption: "",
                width: "50px", // Add fixed width for consistency
                align: "center",
                cellRender: (data: MonThi) => (
                  <Box sx={{ display: 'flex', justifyContent: 'center' }}> {/* Add container for centering */}
                    <ActionMenu>
                      <ActionMenu.Button
                        icon={KebabHorizontalIcon}
                        variant="invisible"
                        sx={{ p: 1 }}
                      >
                        &nbsp;
                      </ActionMenu.Button>
                      <ActionMenu.Overlay width="auto">
                        <ActionList>
                          <ActionList.Item
                            onSelect={() => {
                              setSelectedMonThi(data);
                              setShowMonThiModal(true);
                            }}
                          >
                            <ActionList.LeadingVisual>
                              <PencilIcon />
                            </ActionList.LeadingVisual>
                            {translate("Base.Label.Edit")}
                          </ActionList.Item>
                          <ActionList.Item
                            variant="danger"
                            onSelect={() => {
                              setSelectedMonThi(data);
                              setShowDeleteDialog(true);
                            }}
                          >
                            <ActionList.LeadingVisual>
                              <TrashIcon />
                            </ActionList.LeadingVisual>
                            {translate("Base.Label.Delete")}
                          </ActionList.Item>
                        </ActionList>
                      </ActionMenu.Overlay>
                    </ActionMenu>
                  </Box>
                ),
              },
              {
                dataField: "mon_thi",
                caption: translate("Môn thi"),
              },
              {
                dataField: "mon_thi_en",
                caption: translate("Môn thi (En)"),
              },
              {
                dataField: "thang_diem",
                caption: translate("Thang điểm"),
              },
              {
                dataField: "nhan_xet_chung",
                caption: translate("Nhận xét chung"),
                width: "15%",
                align: "center", // Add center alignment for better checkbox positioning
                cellRender: (data: MonThi) => (
                  <input
                    type="checkbox"
                    checked={data.nhan_xet_chung}
                    readOnly
                    style={{ cursor: 'default' }}
                  />
                )
              },            
            ]}
          />
        </Box>

        {/* Mon Thi Thanh Phan Table */}
        <Box flex="1" minWidth="0">
          <DataTable
            height="calc(100vh - 200px)"
            data={monThiThanhPhans}
            isLoading={isLoading}
            title="Thành phần"
            searchEnable
            paging={{ enable: true }}
            actionComponent={
              <Box sx={{ display: "flex" }}>
                {selectedMonThi && selectedMonThi.id > 0 && (
                  <MyButton
                    text="Thêm thành phần môn"
                    leadingVisual={PlusIcon}
                    size="medium"
                    variant="primary"
                    onClick={() => {
                      setSelectedMonThiThanhPhan({ id: 0 } as MonThiThanhPhan);
                      setShowMonThiThanhPhanModal(true);
                    }}
                  />
                )}
                <MyButton
                  text="Base.Label.Refresh"
                  leadingVisual={SyncIcon}
                  size="medium"
                  sx={{ ml: 2 }}
                  onClick={loadMonThiThanhPhans}
                />
              </Box>
            }
            columns={[
              {
                id: "cmd",
                caption: "",
                width: "50px", // Add fixed width
                align: "center",
                cellRender: (data: MonThiThanhPhan) => (
                  <Box sx={{ display: 'flex', justifyContent: 'center' }}> {/* Add centering container */}
                    <ActionMenu>
                      <ActionMenu.Button
                        icon={KebabHorizontalIcon}
                        variant="invisible"
                        sx={{ p: 1 }}
                      >
                        &nbsp;
                      </ActionMenu.Button>
                      <ActionMenu.Overlay width="auto">
                        <ActionList>
                          <ActionList.Item
                            onSelect={() => {
                              setSelectedMonThiThanhPhan(data);
                              setShowMonThiThanhPhanModal(true);
                            }}
                          >
                            <ActionList.LeadingVisual>
                              <PencilIcon />
                            </ActionList.LeadingVisual>
                            {translate("Base.Label.Edit")}
                          </ActionList.Item>
                          <ActionList.Item
                            variant="danger"
                            onSelect={() => {
                              setSelectedMonThiThanhPhan(data);
                              setShowDeleteThanhPhanDialog(true);
                            }}
                          >
                            <ActionList.LeadingVisual>
                              <TrashIcon />
                            </ActionList.LeadingVisual>
                            {translate("Base.Label.Delete")}
                          </ActionList.Item>
                        </ActionList>
                      </ActionMenu.Overlay>
                    </ActionMenu>
                  </Box>
                ),
              },
              {
                dataField: "diem_thanh_phan",
                caption: translate("Điểm thành phần"),
              },
              {
                dataField: "diem_thanh_phan_en",
                caption: translate("Điểm thành phần (En)"),
              },
              {
                dataField: "thang_diem",
                caption: translate("Thang điểm"),
              },
              {
                dataField: "nhan_xet_rieng",
                caption: translate("Nhận xét riêng"),
                align: "center", 
                cellRender: (data: MonThiThanhPhan) => (
                  <input
                    type="checkbox"
                    checked={data.nhan_xet_rieng}
                    readOnly
                    style={{ cursor: 'default' }}
                  />
                )
              },
              {
                dataField: "trong_so",
                caption: translate("Trọng số"),
                width: "10%",
              },
            ]}
          />
        </Box>
      </Box>

      {/* Modals */}
      {showMonThiModal && selectedMonThi && (
        <MonThiDetailModal
          id={selectedMonThi.id}
          onCancel={() => setShowMonThiModal(false)}
          onSuccess={() => {
            setShowMonThiModal(false);
            loadMonThis();
          }}
        />
      )}

      {showMonThiThanhPhanModal &&
        selectedMonThiThanhPhan &&
        selectedMonThi && (
          <MonThiThanhPhanDetailModal
            id={selectedMonThiThanhPhan.id}
            onCancel={() => setShowMonThiThanhPhanModal(false)}
            onSuccess={() => {
              setShowMonThiThanhPhanModal(false);
              loadMonThiThanhPhans();
            }}
            ex_monthi_id={selectedMonThi.id}
          />
        )}

      {/* Dialogs for deletion confirmation */}
      <DeleteConfirmDialog
        isOpen={showDeleteDialog}
        onClose={() => setShowDeleteDialog(false)}
        onConfirm={handleDeleteMonThi}
      />

      <DeleteConfirmDialog
        isOpen={showDeleteThanhPhanDialog}
        onClose={() => setShowDeleteThanhPhanDialog(false)}
        onConfirm={handleDeleteMonThiThanhPhan}
      />
    </Box>
  );
};

export default MonThiComponent;
